import React, { useState, useEffect } from "react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { useAuth } from "../context/AuthContext";
import { api } from "../services/api";
import {
  SlidersHorizontal,
  DollarSign,
  Package,
  ShieldCheck,
  AlertTriangle,
  Plus,
  Edit2,
  Trash2,
  RefreshCw,
  Lock,
  Upload,
  Loader2,
  Star,
  Link as LinkIcon,
} from "lucide-react";
import { toast } from "sonner";

export function generateSlug(text) {
  if (!text) return "";
  return text
    .normalize("NFD") // split accents from letters
    .replace(/[\u0300-\u036f]/g, "") // remove accents
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "") // remove characters that are not letters, digits, whitespace or hyphen
    .trim()
    .replace(/\s+/g, "-") // replace whitespace with hyphen
    .replace(/-+/g, "-"); // remove duplicate hyphens
}

export default function AdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("metrics"); // metrics, products, orders, audit
  const [metrics, setMetrics] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [manualImageUrl, setManualImageUrl] = useState("");
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  // Helper to add image to form images array
  const addImageToGallery = (url) => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl) return;
    if (!trimmedUrl.startsWith("http://") && !trimmedUrl.startsWith("https://")) {
      toast.error("A URL da imagem deve começar com http:// ou https://");
      return;
    }

    setProductForm((prev) => {
      const currentImages = Array.isArray(prev.images) ? [...prev.images] : [];
      // Check if already in gallery
      if (currentImages.some((img) => img.url === trimmedUrl)) {
        toast.info("Esta imagem já está presente na galeria.");
        return prev;
      }
      const isFirst = currentImages.length === 0;
      const newImages = [...currentImages, { url: trimmedUrl, principal: isFirst }];
      return {
        ...prev,
        images: newImages,
        main_media_url: isFirst ? trimmedUrl : prev.main_media_url || trimmedUrl,
      };
    });
  };

  const handleRemoveImage = (indexToRemove) => {
    setProductForm((prev) => {
      const currentImages = Array.isArray(prev.images) ? [...prev.images] : [];
      const removed = currentImages[indexToRemove];
      const remaining = currentImages.filter((_, idx) => idx !== indexToRemove);

      // If we removed the principal image, make the first remaining the principal
      if (removed?.principal && remaining.length > 0) {
        remaining[0].principal = true;
      }

      const newPrincipalUrl = remaining.find((img) => img.principal)?.url || (remaining.length > 0 ? remaining[0].url : "");

      return {
        ...prev,
        images: remaining,
        main_media_url: newPrincipalUrl,
      };
    });
  };

  const handleSetPrincipalImage = (indexToPrincipal) => {
    setProductForm((prev) => {
      const currentImages = Array.isArray(prev.images) ? [...prev.images] : [];
      const updated = currentImages.map((img, idx) => ({
        ...img,
        principal: idx === indexToPrincipal,
      }));
      const newPrincipalUrl = updated[indexToPrincipal]?.url || "";
      return {
        ...prev,
        images: updated,
        main_media_url: newPrincipalUrl,
      };
    });
  };

  const handleAddManualUrl = (e) => {
    e.preventDefault();
    if (!manualImageUrl.trim()) {
      toast.error("Por favor, digite uma URL válida.");
      return;
    }
    addImageToGallery(manualImageUrl);
    setManualImageUrl("");
  };

  const handleImageFileChange = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // 1. Validate file extension
    const allowedExtensions = ["jpg", "jpeg", "png", "webp"];
    const fileExt = selectedFile.name.split(".").pop().toLowerCase();
    if (!allowedExtensions.includes(fileExt)) {
      toast.error("Formato inválido. Selecione apenas imagens JPG, PNG ou WEBP.");
      e.target.value = "";
      return;
    }

    // 2. Validate MIME type
    const allowedMimeTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedMimeTypes.includes(selectedFile.type)) {
      toast.error("Tipo de arquivo inválido. Apenas JPG, PNG e WEBP são aceitos.");
      e.target.value = "";
      return;
    }

    // 3. Validate size (Max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (selectedFile.size > maxSize) {
      toast.error("O arquivo excede o tamanho máximo permitido de 10 MB.");
      e.target.value = "";
      return;
    }

    // 4. Send multipart/form-data with field name "file"
    setUploadingImage(true);
    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const res = await api.post("/admin/upload-image", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.data?.success && res.data?.url) {
        addImageToGallery(res.data.url);
        toast.success("Imagem enviada com sucesso!");
      } else {
        toast.error(res.data?.message || "Não foi possível enviar a imagem.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Não foi possível enviar a imagem. Tente novamente.");
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  // Edit / Create Product Modal
  const [editingProduct, setEditingProduct] = useState(null);
  const [newProductModal, setNewProductModal] = useState(false);
  const [productForm, setProductForm] = useState({
    name: "",
    slug: "",
    category_id: "cat_lingerie",
    short_description: "",
    description: "",
    price_in_cents: 29900,
    compare_at_price_in_cents: 39900,
    sku: "",
    stock_on_hand: 10,
    main_media_url: "https://images.unsplash.com/photo-1607690702277-ea1684b8aa89",
    is_featured: false,
    status: "published",
  });

  // Refund Modal
  const [refundModal, setRefundModal] = useState(null);
  const [refundReason, setRefundReason] = useState("");

  const loadAllData = async () => {
    try {
      const [mRes, pRes, oRes, aRes] = await Promise.all([
        api.get("/admin/metrics"),
        api.get("/admin/products"),
        api.get("/admin/orders"),
        api.get("/admin/audit-logs"),
      ]);
      setMetrics(mRes.data);
      setProducts(pRes.data);
      setOrders(oRes.data);
      setAuditLogs(aRes.data);
    } catch (err) {
      toast.error("Erro ao carregar dados do painel administrativo.");
    }
  };

  useEffect(() => {
    if (user && (user.role === "admin" || user.role === "catalog_manager" || user.role === "support")) {
      loadAllData();
    }
  }, [user]);

  if (!user || (user.role !== "admin" && user.role !== "catalog_manager" && user.role !== "support")) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] text-white flex flex-col justify-between">
        <Header />
        <div className="py-24 text-center max-w-md mx-auto p-8 rounded-3xl bg-[#121214] border border-white/10 space-y-4">
          <ShieldCheck className="w-12 h-12 text-[#FF8C69] mx-auto" />
          <h2 className="font-heading text-2xl font-bold">Área Restrita</h2>
          <p className="text-xs text-[#A1A1AA]">
            Entre na sua conta para continuar.
          </p>
        </div>
        <Footer />
      </div>
    );
  }

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await api.put(`/admin/products/${editingProduct.product_id}`, productForm);
        toast.success("Produto atualizado com sucesso.");
        setEditingProduct(null);
      } else {
        await api.post("/admin/products", productForm);
        toast.success("Produto cadastrado com sucesso.");
        setNewProductModal(false);
      }
      loadAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar produto.");
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Deseja realmente arquivar este produto?")) return;
    try {
      await api.delete(`/admin/products/${productId}`);
      toast.success("Produto arquivado com sucesso.");
      loadAllData();
    } catch (err) {
      toast.error("Erro ao arquivar produto.");
    }
  };

  const handleUpdateOrderStatus = async (orderId, newFulfillment, trackingCode) => {
    try {
      await api.put(`/admin/orders/${orderId}/status`, {
        fulfillment_status: newFulfillment,
        tracking_code: trackingCode,
        notes: `Atualizado por ${user.name}`,
      });
      toast.success("Status do pedido atualizado.");
      loadAllData();
    } catch (err) {
      toast.error("Erro ao atualizar status.");
    }
  };

  const handleProcessRefund = async (e) => {
    e.preventDefault();
    if (!refundModal || !refundReason.trim()) {
      toast.error("Informe o motivo do reembolso.");
      return;
    }
    try {
      await api.post(`/admin/orders/${refundModal.order_id}/refund`, {
        reason: refundReason.trim(),
      });
      toast.success("Reembolso processado com sucesso.");
      setRefundModal(null);
      setRefundReason("");
      loadAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao processar reembolso.");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white flex flex-col justify-between">
      <div>
        <Header />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          
          {/* Header Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-8 border-b border-white/10 mb-8">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs tracking-widest text-[#FF8C69] uppercase font-bold">
                  Painel de Controle
                </span>
                <span className="px-2 py-0.5 rounded-full bg-[#18181B] border border-white/10 text-[10px] font-bold text-[#FF8C69]">
                  {user.role.toUpperCase()}
                </span>
              </div>
              <h1 className="font-heading text-3xl font-bold mt-1 text-white">
                Gestão da Loja joycesxyloja.com
              </h1>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={loadAllData}
                data-testid="admin-refresh-btn"
                className="p-2.5 rounded-xl bg-[#121214] border border-white/10 text-white hover:text-[#FF8C69]"
                title="Recarregar"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
              {user.role === "admin" && (
                <button
                  onClick={() => {
                    setEditingProduct(null);
                    setSlugManuallyEdited(false);
                    setProductForm({
                      name: "",
                      slug: "",
                      category_id: "cat_lingerie",
                      short_description: "",
                      description: "",
                      price_in_cents: 29900,
                      compare_at_price_in_cents: 39900,
                      sku: `JOY-${Date.now().toString().slice(-4)}`,
                      stock_on_hand: 15,
                      main_media_url: "",
                      images: [],
                      is_featured: false,
                      status: "published",
                    });
                    setNewProductModal(true);
                  }}
                  data-testid="admin-create-product-btn"
                  className="px-4 py-2.5 rounded-xl bg-[#FF8C69] hover:bg-[#FFA285] text-xs font-bold text-[#0A0A0B] flex items-center gap-2 shadow-lg"
                >
                  <Plus className="w-4 h-4" />
                  <span>Cadastrar Produto</span>
                </button>
              )}
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex items-center gap-2 p-1.5 bg-[#121214] rounded-2xl mb-8 border border-white/10 max-w-fit text-xs font-bold">
            <button
              onClick={() => setTab("metrics")}
              data-testid="tab-metrics"
              className={`px-4 py-2 rounded-xl transition-all ${
                tab === "metrics" ? "bg-[#FF8C69] text-[#0A0A0B] shadow-md" : "text-[#A1A1AA] hover:text-white"
              }`}
            >
              Métricas & Vendas
            </button>
            <button
              onClick={() => setTab("products")}
              data-testid="tab-products"
              className={`px-4 py-2 rounded-xl transition-all ${
                tab === "products" ? "bg-[#FF8C69] text-[#0A0A0B] shadow-md" : "text-[#A1A1AA] hover:text-white"
              }`}
            >
              Produtos & Estoque ({products.length})
            </button>
            <button
              onClick={() => setTab("orders")}
              data-testid="tab-orders"
              className={`px-4 py-2 rounded-xl transition-all ${
                tab === "orders" ? "bg-[#FF8C69] text-[#0A0A0B] shadow-md" : "text-[#A1A1AA] hover:text-white"
              }`}
            >
              Pedidos ({orders.length})
            </button>
            {user.role === "admin" && (
              <button
                onClick={() => setTab("audit")}
                data-testid="tab-audit"
                className={`px-4 py-2 rounded-xl transition-all ${
                  tab === "audit" ? "bg-[#FF8C69] text-[#0A0A0B] shadow-md" : "text-[#A1A1AA] hover:text-white"
                }`}
              >
                Auditoria ({auditLogs.length})
              </button>
            )}
          </div>

          {/* TAB 1: METRICS */}
          {tab === "metrics" && metrics && (
            <div className="space-y-8 animate-in fade-in">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                
                <div className="p-6 rounded-3xl bg-[#121214] border border-white/10 space-y-2 shadow-xl">
                  <div className="flex items-center justify-between text-[#A1A1AA] text-xs">
                    <span>Faturamento Total</span>
                    <DollarSign className="w-5 h-5 text-[#FF8C69]" />
                  </div>
                  <h3 className="font-heading text-3xl font-bold text-[#FF8C69]">
                    {metrics.formatted_total_revenue}
                  </h3>
                  <p className="text-[11px] text-[#10B981]">InfinitePay Pix e Cartão</p>
                </div>

                <div className="p-6 rounded-3xl bg-[#121214] border border-white/10 space-y-2 shadow-xl">
                  <div className="flex items-center justify-between text-[#A1A1AA] text-xs">
                    <span>Total de Pedidos</span>
                    <Package className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-heading text-3xl font-bold text-white">
                    {metrics.total_orders}
                  </h3>
                  <p className="text-[11px] text-[#A1A1AA]">{metrics.paid_orders} pagos • {metrics.pending_orders} pendentes</p>
                </div>

                <div className="p-6 rounded-3xl bg-[#121214] border border-white/10 space-y-2 shadow-xl">
                  <div className="flex items-center justify-between text-[#A1A1AA] text-xs">
                    <span>Reservas de Estoque</span>
                    <Lock className="w-5 h-5 text-amber-400" />
                  </div>
                  <h3 className="font-heading text-3xl font-bold text-amber-400">
                    {metrics.active_stock_reservations}
                  </h3>
                  <p className="text-[11px] text-[#A1A1AA]">Locks de checkout ativos (15 min)</p>
                </div>

                <div className="p-6 rounded-3xl bg-[#121214] border border-white/10 space-y-2 shadow-xl">
                  <div className="flex items-center justify-between text-[#A1A1AA] text-xs">
                    <span>Total de Produtos</span>
                    <SlidersHorizontal className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-heading text-3xl font-bold text-white">
                    {metrics.total_products}
                  </h3>
                  <p className="text-[11px] text-[#A1A1AA]">Catálogo ativo na vitrine</p>
                </div>

              </div>

              {/* Low Stock Alert Table */}
              {metrics.low_stock_items?.length > 0 && (
                <div className="p-6 rounded-3xl bg-[#121214] border border-amber-500/30 space-y-4">
                  <div className="flex items-center gap-2 text-amber-400 text-sm font-bold">
                    <AlertTriangle className="w-5 h-5" />
                    <span>Estoque Baixo (≤ 3 unidades)</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {metrics.low_stock_items.map((it) => (
                      <div key={it.product_id} className="p-3.5 rounded-2xl bg-[#18181B] border border-white/10 text-xs">
                        <p className="font-bold text-white truncate">{it.name}</p>
                        <p className="text-[11px] text-[#A1A1AA]">SKU: {it.sku}</p>
                        <div className="flex justify-between items-center mt-2 pt-2 border-t border-white/10">
                          <span className="text-[#A1A1AA]">Físico: {it.stock_on_hand}</span>
                          <span className="text-amber-400 font-bold">Reservados: {it.stock_reserved}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 2: PRODUCTS */}
          {tab === "products" && (
            <div className="rounded-3xl bg-[#121214] border border-white/10 overflow-hidden shadow-xl animate-in fade-in">
              <div className="p-6 border-b border-white/10 flex justify-between items-center">
                <h3 className="font-heading text-lg font-bold text-white">Catálogo & Estoque</h3>
                <span className="text-xs text-[#A1A1AA]">{products.length} itens</span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-[#18181B] text-[#A1A1AA] border-b border-white/10">
                    <tr>
                      <th className="p-4">Produto / SKU</th>
                      <th className="p-4">Preço</th>
                      <th className="p-4">Físico</th>
                      <th className="p-4">Reservado</th>
                      <th className="p-4">Disponível</th>
                      <th className="p-4">Status</th>
                      <th className="p-4 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {products.map((p) => (
                      <tr key={p.product_id} className="hover:bg-white/5 transition-colors">
                        <td className="p-4 flex items-center gap-3">
                          <img
                            src={p.main_media_url}
                            alt={p.name}
                            className="w-12 h-14 object-cover rounded-xl bg-[#18181B]"
                          />
                          <div>
                            <p className="font-bold text-white">{p.name}</p>
                            <p className="text-[11px] text-[#A1A1AA] font-mono">{p.sku}</p>
                          </div>
                        </td>
                        <td className="p-4 font-heading font-bold text-[#FF8C69]">
                          {p.formatted_price}
                        </td>
                        <td className="p-4 font-mono">{p.stock_on_hand}</td>
                        <td className="p-4 font-mono text-amber-400">{p.stock_reserved}</td>
                        <td className="p-4 font-mono font-bold text-[#10B981]">{p.stock_available}</td>
                        <td className="p-4">
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-[#10B981]/20 text-[#10B981]">
                            {p.status}
                          </span>
                        </td>
                        <td className="p-4 text-right space-x-2">
                          <button
                            onClick={() => {
                              const pImages = Array.isArray(p.images) && p.images.length > 0
                                ? p.images
                                : p.main_media_url
                                ? [{ url: p.main_media_url, principal: true }]
                                : [];
                              setEditingProduct(p);
                              setSlugManuallyEdited(Boolean(p.slug));
                              setProductForm({ ...p, images: pImages });
                            }}
                            data-testid={`btn-edit-prod-${p.product_id}`}
                            className="p-2 rounded-xl bg-[#18181B] text-white hover:text-[#FF8C69] border border-white/10"
                            title="Editar"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          {user.role === "admin" && (
                            <button
                              onClick={() => handleDeleteProduct(p.product_id)}
                              data-testid={`btn-delete-prod-${p.product_id}`}
                              className="p-2 rounded-xl bg-[#18181B] text-red-400 hover:bg-red-500/20 border border-white/10"
                              title="Arquivar"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB 3: ORDERS */}
          {tab === "orders" && (
            <div className="rounded-3xl bg-[#121214] border border-white/10 overflow-hidden shadow-xl animate-in fade-in">
              <div className="p-6 border-b border-white/10 flex justify-between items-center">
                <h3 className="font-heading text-lg font-bold text-white">Pedidos & Logística</h3>
                <span className="text-xs text-[#A1A1AA]">{orders.length} pedidos</span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-[#18181B] text-[#A1A1AA] border-b border-white/10">
                    <tr>
                      <th className="p-4">Pedido</th>
                      <th className="p-4">Cliente</th>
                      <th className="p-4">Total</th>
                      <th className="p-4">Pagamento</th>
                      <th className="p-4">Envio</th>
                      <th className="p-4">Rastreio</th>
                      <th className="p-4 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {orders.map((o) => (
                      <tr key={o.order_id} className="hover:bg-white/5 transition-colors">
                        <td className="p-4">
                          <p className="font-heading font-bold text-white">#{o.public_order_number}</p>
                          <p className="text-[10px] text-[#A1A1AA]">
                            {new Date(o.created_at).toLocaleDateString("pt-BR")}
                          </p>
                        </td>
                        <td className="p-4">
                          <p className="font-bold text-white">{o.customer?.name}</p>
                          <p className="text-[11px] text-[#A1A1AA]">{o.customer?.email}</p>
                        </td>
                        <td className="p-4 font-heading font-bold text-[#FF8C69]">
                          {o.formatted_total}
                        </td>
                        <td className="p-4">
                          <span
                            className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${
                              o.payment_status === "paid"
                                ? "bg-[#10B981]/20 text-[#10B981]"
                                : o.payment_status === "refunded"
                                ? "bg-purple-500/20 text-purple-300"
                                : "bg-amber-500/20 text-amber-300"
                            }`}
                          >
                            {o.payment_status}
                          </span>
                        </td>
                        <td className="p-4">
                          <select
                            value={o.fulfillment_status}
                            onChange={(e) => handleUpdateOrderStatus(o.order_id, e.target.value, o.tracking_code)}
                            className="bg-[#18181B] border border-white/10 rounded-lg py-1 px-2 text-[11px] text-white"
                          >
                            <option value="pending">Pendente</option>
                            <option value="processing">Embalando</option>
                            <option value="shipped">Despachado Correios</option>
                            <option value="delivered">Entregue</option>
                            <option value="cancelled">Cancelado</option>
                          </select>
                        </td>
                        <td className="p-4 font-mono font-bold text-white">
                          {o.tracking_code}
                        </td>
                        <td className="p-4 text-right">
                          {o.payment_status === "paid" && user.role === "admin" && (
                            <button
                              onClick={() => setRefundModal(o)}
                              data-testid={`btn-refund-${o.order_id}`}
                              className="px-3 py-1.5 rounded-xl bg-red-500/20 border border-red-500/30 text-red-400 hover:bg-red-500/30 text-[11px] font-bold"
                            >
                              Reembolsar
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB 4: AUDIT LOGS */}
          {tab === "audit" && user.role === "admin" && (
            <div className="rounded-3xl bg-[#121214] border border-white/10 overflow-hidden shadow-xl animate-in fade-in">
              <div className="p-6 border-b border-white/10 flex justify-between items-center">
                <h3 className="font-heading text-lg font-bold text-white">Auditoria de Ações</h3>
                <span className="text-xs text-[#A1A1AA]">{auditLogs.length} logs</span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-[#18181B] text-[#A1A1AA] border-b border-white/10">
                    <tr>
                      <th className="p-4">Data / Hora</th>
                      <th className="p-4">Usuário / Papel</th>
                      <th className="p-4">Ação</th>
                      <th className="p-4">Recurso</th>
                      <th className="p-4">Detalhes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5 font-mono text-[11px]">
                    {auditLogs.map((log) => (
                      <tr key={log.audit_id} className="hover:bg-white/5 transition-colors">
                        <td className="p-4 text-[#A1A1AA]">
                          {new Date(log.timestamp).toLocaleString("pt-BR")}
                        </td>
                        <td className="p-4 text-white">
                          <span className="font-sans font-bold">{log.user_email}</span>
                          <span className="block text-[10px] text-[#FF8C69]">{log.role}</span>
                        </td>
                        <td className="p-4 text-[#FF8C69] font-bold">{log.action}</td>
                        <td className="p-4 text-[#A1A1AA]">{log.resource}</td>
                        <td className="p-4 text-white font-sans">{log.reason || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </main>
      </div>

      {/* Product Form Modal */}
      {(newProductModal || editingProduct) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in overflow-y-auto">
          <div className="relative w-full max-w-2xl rounded-3xl bg-[#121214] border border-white/10 shadow-2xl p-6 sm:p-8 text-white my-8 space-y-6">
            <h3 className="font-heading text-2xl font-bold text-white">
              {editingProduct ? "Editar Produto" : "Cadastrar Novo Produto"}
            </h3>

            <form onSubmit={handleSaveProduct} className="space-y-4 text-xs">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#A1A1AA] mb-1 font-bold">Nome do Produto</label>
                  <input
                    type="text"
                    required
                    value={productForm.name}
                    onChange={(e) => {
                      const newName = e.target.value;
                      setProductForm((prev) => ({
                        ...prev,
                        name: newName,
                        slug: slugManuallyEdited ? prev.slug : generateSlug(newName),
                      }));
                    }}
                    data-testid="admin-form-prod-name"
                    className="w-full bg-[#18181B] border border-white/10 rounded-xl p-3 text-white focus:border-[#FF8C69]"
                  />
                </div>

                <div>
                  <label className="block text-[#A1A1AA] mb-1 font-bold">Slug URL</label>
                  <input
                    type="text"
                    required
                    value={productForm.slug}
                    onChange={(e) => {
                      setSlugManuallyEdited(true);
                      setProductForm({ ...productForm, slug: e.target.value });
                    }}
                    data-testid="admin-form-prod-slug"
                    className="w-full bg-[#18181B] border border-white/10 rounded-xl p-3 text-white focus:border-[#FF8C69]"
                  />
                </div>

                <div>
                  <label className="block text-[#A1A1AA] mb-1 font-bold">Preço em Centavos (ex: 29900 = R$ 299,00)</label>
                  <input
                    type="number"
                    required
                    value={productForm.price_in_cents}
                    onChange={(e) => setProductForm({ ...productForm, price_in_cents: parseInt(e.target.value, 10) })}
                    data-testid="admin-form-prod-price"
                    className="w-full bg-[#18181B] border border-white/10 rounded-xl p-3 text-white focus:border-[#FF8C69]"
                  />
                </div>

                <div>
                  <label className="block text-[#A1A1AA] mb-1 font-bold">SKU</label>
                  <input
                    type="text"
                    required
                    value={productForm.sku}
                    onChange={(e) => setProductForm({ ...productForm, sku: e.target.value })}
                    data-testid="admin-form-prod-sku"
                    className="w-full bg-[#18181B] border border-white/10 rounded-xl p-3 text-white focus:border-[#FF8C69]"
                  />
                </div>

                <div>
                  <label className="block text-[#A1A1AA] mb-1 font-bold">Estoque Inicial</label>
                  <input
                    type="number"
                    required
                    value={productForm.stock_on_hand}
                    onChange={(e) => setProductForm({ ...productForm, stock_on_hand: parseInt(e.target.value, 10) })}
                    data-testid="admin-form-prod-stock"
                    className="w-full bg-[#18181B] border border-white/10 rounded-xl p-3 text-white focus:border-[#FF8C69]"
                  />
                </div>
              </div>

              {/* Bloco Visual Único: Imagens do produto */}
              <div className="p-4 rounded-2xl bg-[#18181B] border border-white/10 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-white font-bold text-xs">Imagens do produto</span>
                  <span className="text-[11px] font-bold text-[#FF8C69] bg-[#FF8C69]/10 px-2.5 py-0.5 rounded-full border border-[#FF8C69]/20">
                    {(productForm.images || []).length} {(productForm.images || []).length === 1 ? 'imagem' : 'imagens'}
                  </span>
                </div>

                {/* Controles alinhados: [Escolher imagem] como principal + [Adicionar por URL] */}
                <div className="flex flex-col sm:flex-row gap-2 pt-1">
                  {/* Botão Principal: Escolher imagem do computador */}
                  <label
                    className={`py-2.5 px-4 rounded-xl text-xs font-bold flex items-center justify-center gap-2 cursor-pointer transition-all shadow-md shrink-0 ${
                      uploadingImage
                        ? "bg-[#FF8C69]/50 text-[#0A0A0B] cursor-not-allowed opacity-70"
                        : "bg-[#FF8C69] hover:bg-[#FFA285] text-[#0A0A0B]"
                    }`}
                    data-testid="admin-choose-image-btn"
                  >
                    {uploadingImage ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Enviando imagem...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        <span>Escolher imagem</span>
                      </>
                    )}
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                      onChange={handleImageFileChange}
                      disabled={uploadingImage}
                      className="hidden"
                    />
                  </label>

                  {/* Adicionar por URL (Secundário) */}
                  <div className="flex-1 flex gap-2">
                    <input
                      type="url"
                      value={manualImageUrl}
                      onChange={(e) => setManualImageUrl(e.target.value)}
                      data-testid="admin-form-prod-image"
                      placeholder="https://exemplo.com/foto.jpg"
                      className="flex-1 bg-[#121214] border border-white/10 rounded-xl px-3 py-2 text-xs text-white placeholder-[#A1A1AA]/40 focus:border-[#FF8C69]"
                    />
                    <button
                      type="button"
                      onClick={handleAddManualUrl}
                      data-testid="admin-gallery-add-url-btn"
                      className="px-3.5 py-2 rounded-xl bg-[#121214] hover:bg-white/10 text-white border border-white/10 text-xs font-bold transition-all flex items-center gap-1.5 shrink-0"
                    >
                      <LinkIcon className="w-3.5 h-3.5" />
                      <span>Adicionar URL</span>
                    </button>
                  </div>
                </div>

                {/* Galeria de Miniaturas */}
                {(productForm.images || []).length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1">
                    {(productForm.images || []).map((img, idx) => (
                      <div
                        key={idx}
                        className={`relative rounded-xl overflow-hidden border transition-all group bg-[#121214] ${
                          img.principal ? "border-[#FF8C69] ring-2 ring-[#FF8C69]/30" : "border-white/10"
                        }`}
                        data-testid={`gallery-item-${idx}`}
                      >
                        <div className="aspect-[4/3] w-full bg-[#0A0A0B] relative">
                          <img
                            src={img.url}
                            alt={`Foto ${idx + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1557821552-17105176677c"; }}
                          />
                          {img.principal && (
                            <span className="absolute top-1.5 left-1.5 px-2 py-0.5 rounded-md bg-[#FF8C69] text-[#0A0A0B] font-bold text-[9px] flex items-center gap-1 shadow-md">
                              <Star className="w-2.5 h-2.5 fill-[#0A0A0B]" />
                              <span>Capa</span>
                            </span>
                          )}
                        </div>

                        <div className="p-2 space-y-1.5">
                          <p className="text-[10px] text-[#A1A1AA] truncate" title={img.url}>
                            {img.url}
                          </p>
                          <div className="flex gap-1">
                            {!img.principal && (
                              <button
                                type="button"
                                onClick={() => handleSetPrincipalImage(idx)}
                                data-testid={`gallery-set-principal-btn-${idx}`}
                                className="flex-1 py-1 rounded-lg bg-[#18181B] hover:bg-[#FF8C69] hover:text-[#0A0A0B] text-[10px] font-bold text-[#FF8C69] border border-white/10 transition-all text-center"
                              >
                                Capa
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemoveImage(idx)}
                              data-testid={`gallery-remove-btn-${idx}`}
                              className="py-1 px-2 rounded-lg bg-[#18181B] hover:bg-red-500/20 text-red-400 border border-white/10 text-[10px] font-bold transition-all"
                              title="Remover imagem"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-5 text-center rounded-xl border border-dashed border-white/10 bg-[#121214]/50">
                    <p className="text-[11px] text-[#A1A1AA]">Nenhuma imagem adicionada. Clique em "Escolher imagem" ou informe uma URL.</p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-[#A1A1AA] mb-1 font-bold">Descrição Curta</label>
                <input
                  type="text"
                  required
                  value={productForm.short_description}
                  onChange={(e) => setProductForm({ ...productForm, short_description: e.target.value })}
                  className="w-full bg-[#18181B] border border-white/10 rounded-xl p-3 text-white focus:border-[#FF8C69]"
                />
              </div>

              <div>
                <label className="block text-[#A1A1AA] mb-1 font-bold">Descrição Completa</label>
                <textarea
                  rows={3}
                  value={productForm.description}
                  onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                  className="w-full bg-[#18181B] border border-white/10 rounded-xl p-3 text-white focus:border-[#FF8C69]"
                />
              </div>

              <div className="flex gap-4 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => {
                    setEditingProduct(null);
                    setNewProductModal(false);
                  }}
                  className="flex-1 py-3 rounded-xl bg-[#18181B] border border-white/10 text-white"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  data-testid="admin-form-submit-btn"
                  className="flex-1 py-3 rounded-xl bg-[#FF8C69] hover:bg-[#FFA285] text-[#0A0A0B] font-bold shadow-lg"
                >
                  Salvar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Refund Modal */}
      {refundModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in">
          <div className="w-full max-w-md rounded-3xl bg-[#121214] border border-white/10 shadow-2xl p-6 sm:p-8 text-white space-y-6">
            <h3 className="font-heading text-xl font-bold text-red-400">
              Reembolsar Pedido #{refundModal.public_order_number}
            </h3>
            <p className="text-xs text-[#A1A1AA]">
              Valor do estorno: <strong className="text-[#FF8C69]">{refundModal.formatted_total}</strong>
            </p>

            <form onSubmit={handleProcessRefund} className="space-y-4 text-xs">
              <div>
                <label className="block text-[#A1A1AA] mb-1 font-bold">Motivo do Reembolso:</label>
                <textarea
                  required
                  rows={3}
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="Motivo para registro de auditoria..."
                  data-testid="refund-reason-input"
                  className="w-full bg-[#18181B] border border-white/10 rounded-xl p-3 text-white focus:border-red-400"
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setRefundModal(null)}
                  className="flex-1 py-3 rounded-xl bg-[#18181B] border border-white/10 text-[#A1A1AA]"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  data-testid="btn-confirm-refund-submit"
                  className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-500 text-white font-bold"
                >
                  Confirmar Estorno
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
